import type { Mesh, MeshBasicMaterial, SceneControl, Texture } from '@anov/3d-core'
import { EquirectangularReflectionMapping, Euler, MathUtils, Object3D, PMREMGenerator, SRGBColorSpace, SphereGeometry, TextureLoader, lib, use } from '@anov/3d-core'
import type { HDROptions } from '../types'

export class HDRSky extends Object3D {
  private _currentHDR = ''
  private _envMap: Texture | null = null
  private _hdrBackgroundMesh: Mesh | null = null
  private options: HDROptions

  private _loaded = false
  private _loadingPromise: Promise<Texture> | null = null
  private pendingRotate?: number
  private pendingIntensity?: number
  private sceneControl?: SceneControl

  constructor(options: HDROptions, sceneControl?: SceneControl) {
    super()
    this.options = { ...options }
    this.sceneControl = sceneControl
  }

  get rotate() {
    return this.options.rotate
  }

  get intensity() {
    return this.options.intensity
  }

  set rotate(rotate: number) {
    this.setRotate(rotate)
  }

  set intensity(intensity: number) {
    this.setIntensity(intensity)
  }

  public async setPath(path: string) {
    this.pendingRotate = this.options.rotate
    this.pendingIntensity = this.options.intensity
    const { scene, renderer, camera, envManager } = use.useScene(this.sceneControl)

    if (this._currentHDR === path)
      return
    this._loaded = false

    this.options.path = path
    const isHDR = /\.hdr$/i.test(path)
    // const pathUrl = path.replace(/\.png$/, '.hdr') // png 替换成 hdr

    if (this._envMap)
      this._envMap.dispose()
    // 清理旧的背景球
    if (this._hdrBackgroundMesh) {
      this.remove(this._hdrBackgroundMesh)
      this._hdrBackgroundMesh.geometry.dispose()
      ;(this._hdrBackgroundMesh.material as MeshBasicMaterial).dispose()
      this._hdrBackgroundMesh = undefined
    }
    this._loadingPromise = new Promise<Texture>((resolve, reject) => {
      const onLoad = (texture: Texture) => {
        texture.mapping = EquirectangularReflectionMapping

        // HDR / LDR 都可以走 PMREM（推荐统一）
        envManager.useHDR(texture, renderer)

        this._currentHDR = path
        scene.background = texture

        this._envMap = texture
        this._loaded = true

        this.flushPending()
        resolve(texture)
      }

      const onError = (error: any) => {
        console.error('加载环境贴图失败:', error)
        scene.background = null
        reject(error)
      }

      if (isHDR) {
        // ✅ HDR
        new lib.RGBELoader().load(path, onLoad, undefined, onError)
      }
      else {
        // ✅ 普通图片
        new TextureLoader().load(path, (texture) => {
          // 关键：颜色空间（否则会偏灰）
          texture.colorSpace = SRGBColorSpace

          onLoad(texture)
        }, undefined, onError)
      }
      // const rgbeloader = new lib.RGBELoader()
      // rgbeloader.load(pathUrl, (hdrTex) => {
      //   hdrTex.mapping = EquirectangularReflectionMapping
      //   envManager.useHDR(hdrTex, renderer)
      //   // 创建 HDR 背景球
      //   // const geometry = new SphereGeometry(1000, 60, 40);
      //   // geometry.scale(-1, 1, 1); // 反转法线
      //   // const material = new MeshBasicMaterial({ map: hdrTex });
      //   // this._hdrBackgroundMesh = new Mesh(geometry, material);
      //   // this.add(this._hdrBackgroundMesh);

      //   this._currentHDR = path
      //   scene.background = hdrTex
      //   this._loaded = true
      //   // ✅加载完成 → 自动补执行 rotate/intensity
      //   this.flushPending()
      //   resolve()
      //   // hdrTex.dispose()
      // }, undefined, (error) => {
      //   console.error('加载环境贴图失败:', error)
      //   // 如果环境贴图加载失败，使用默认环境光
      //   scene.background = null
      //   // pmremGen.dispose() // 出错也要释放资源
      //   reject(error)
      // })
    })
    return this._loadingPromise
  }

  public setRotate(value: number) {
    // ✅没 ready 就缓存
    if (!this._loaded) {
      this.pendingRotate = value
      return
    }
    if (this.options.rotate === value)
      return
    this.options.rotate = value
    const { scene } = use.useScene(this.sceneControl)
    const rotationY = MathUtils.degToRad(value)
    const euler = new Euler(0, rotationY, 0)
    scene.backgroundRotation.copy(euler)
    scene.environmentRotation.copy(euler)
  }

  public setIntensity(value: number) {
    // ✅没 ready 就缓存
    if (!this._loaded) {
      this.pendingIntensity = value
      return
    }
    if (this.options.intensity === value)
      return
    this.options.intensity = value
    const { scene } = use.useScene(this.sceneControl)
    scene.backgroundIntensity = value / 100
    scene.environmentIntensity = value / 100
  }

  private flushPending() {
    if (this.pendingRotate !== undefined) {
      this.setRotate(this.pendingRotate)
      this.pendingRotate = undefined
    }

    if (this.pendingIntensity !== undefined) {
      this.setIntensity(this.pendingIntensity)
      this.pendingIntensity = undefined
    }
  }
}
